import React, { useEffect, useState } from "react";
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  TextField,
  MenuItem,
  Button,
  InputAdornment,
  Modal,
  Autocomplete,
  Divider,
} from "@mui/material";
import { Delete, Search, Add, ReceiptLong, Print } from "@mui/icons-material";
import axios from "axios";
import apiEndpoints from "../../apiconfig";
import { useSnackbar } from "../../Components/Snackbar/SnackbarContext";

const PAYMENT_MODES = [
  { value: "cash", label: "Cash" },
  { value: "card", label: "Card" },
  { value: "upi", label: "UPI" },
  { value: "bank", label: "Bank Transfer" },
];
const modeLabel = (mode) =>
  PAYMENT_MODES.find((m) => m.value === mode)?.label || (mode || "").toUpperCase();

// Receipt number: MODE-YYYYMMDD-NN, e.g. BANK-20260928-01 (NN = sequential receipt_no).
// Payments without a receipt number (failed / pending / older rows) show their own ID.
const RECEIPT_PREFIX = { cash: "CASH", bank: "BANK", upi: "UPI", card: "CARD" };
const receiptNoOf = (p) => {
  if (!p) return "";
  if (p.receipt_no && p.payment_date) {
    const prefix = RECEIPT_PREFIX[p.mode] || "CASH";
    const ymd = String(p.payment_date).slice(0, 10).replace(/-/g, "");
    return `${prefix}-${ymd}-${String(p.receipt_no).padStart(2, "0")}`;
  }
  return p.transcation_id || String(p.id ?? "");
};

const Payments = () => {
  const { showSnackbar } = useSnackbar();
  const [payments, setPayments] = useState([]);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);

  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [saving, setSaving] = useState(false);
  const [gstSettings, setGstSettings] = useState({ gstin: "", sac_code: "", type: "CGST_SGST" });
  const [modeFilter, setModeFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [feeRequests, setFeeRequests] = useState([]);

  const [openAdd, setOpenAdd] = useState(false);
  const [openReceipt, setOpenReceipt] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [studentDetails, setStudentDetails] = useState(null);
  const [errors, setErrors] = useState({});
  const token = localStorage.getItem("token");

  const numberToWords = (num) => {
    const a = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
    const b = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
    const g = ['', 'Thousand', 'Million', 'Billion', 'Trillion'];
    
    const parseBasic = (n) => {
      let str = '';
      if (n >= 100) {
        str += a[Math.floor(n / 100)] + ' Hundred ';
        n %= 100;
      }
      if (n >= 20) {
        str += b[Math.floor(n / 10)] + ' ';
        n %= 10;
      }
      if (n > 0) {
        str += a[n] + ' ';
      }
      return str.trim();
    };

    // Rupees and paise separately — GST can leave paise (e.g. 1525.42)
    const totalPaise = Math.round((Number(num) || 0) * 100);
    let rupees = Math.floor(totalPaise / 100);
    const paise = totalPaise % 100;

    let result = '';
    let parts = [];

    while (rupees > 0) {
      parts.push(rupees % 1000);
      rupees = Math.floor(rupees / 1000);
    }

    for (let i = 0; i < parts.length; i++) {
      if (parts[i] === 0) continue;
      let words = parseBasic(parts[i]);
      result = words + ' ' + g[i] + ' ' + result;
    }

    result = result.trim() || 'Zero';
    if (paise > 0) result += ' and ' + parseBasic(paise) + ' Paise';
    return result + ' Only';
  };

  // School fees are education services, exempt from GST, so every receipt shows
  // GST at 0% unless the payment itself stored a different rate when recorded
  // (inclusive of the amount paid — totals never change).
  const gstBreakdown = (payment) => {
    if (!payment) return null;
    const hasSnapshot = payment.gst_rate !== undefined && payment.gst_rate !== null && payment.gst_rate !== "";
    const amount = Number(payment.amount) || 0;
    const rate = hasSnapshot ? Number(payment.gst_rate) : 0;
    const gst = hasSnapshot ? Number(payment.gst_amount) || 0 : 0;
    const taxable = hasSnapshot ? Number(payment.taxable_amount) || amount : amount;
    const type = hasSnapshot ? payment.gst_type : gstSettings.type;
    const mode = hasSnapshot && payment.gst_mode === "exclusive" ? "exclusive" : "inclusive";
    const half = Math.round((gst / 2) * 100) / 100;
    return {
      exempt: rate === 0,
      mode,
      // exclusive: GST was collected on top of the amount that counts towards the fee
      total: mode === "exclusive" ? Math.round((amount + gst) * 100) / 100 : amount,
      gstin: (hasSnapshot && payment.gstin) || gstSettings.gstin || profileGstin() || "",
      sac: (hasSnapshot && payment.sac_code) || gstSettings.sac_code || "",
      taxable,
      lines:
        type === "IGST"
          ? [{ label: `IGST @ ${rate}%`, amount: gst }]
          : [
              { label: `CGST @ ${rate / 2}%`, amount: half },
              { label: `SGST @ ${rate / 2}%`, amount: Math.round((gst - half) * 100) / 100 },
            ],
    };
  };

  const profileGstin = () => {
    try {
      return (JSON.parse(localStorage.getItem("user") || "{}")?.gstIn || "").toUpperCase();
    } catch (e) {
      return "";
    }
  };

  const formatClassName = (name) => {
    if (!name) return 'N/A';
    const isGuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(name) || /^[0-9a-f]{32}$/i.test(name);
    return isGuid ? 'N/A' : name;
  };

  const handlePrint = (payment, studentDetails) => {
    const printWindow = window.open("", "_blank", "width=850,height=700");
    const loggedInUser = (() => {
      try {
        const item = localStorage.getItem("user");
        if (!item || item === "undefined" || item === "null") return {};
        return JSON.parse(item) || {};
      } catch (e) {
        return {};
      }
    })();
    const schoolLogo = window.location.origin + '/schoollogo.png';
    const orgName = loggedInUser.organization_name || "ROOTS MONTESSORI SCHOOL";
    
    const addressStr = [
      loggedInUser.address,
      loggedInUser.city,
      loggedInUser.state,
      loggedInUser.country,
      loggedInUser.pincode ? `PIN: ${loggedInUser.pincode}` : ""
    ].filter(Boolean).join(", ") || "12, Green Street, Bangalore, Karnataka 560102";
    
    const contactStr = [
      loggedInUser.phone ? `Phone: ${loggedInUser.phone}` : "",
      loggedInUser.email ? `Email: ${loggedInUser.email}` : ""
    ].filter(Boolean).join(" | ") || "Phone No. : 080-2223344 | Email: info@rootsmontessori.com";

    const formattedDate = payment.payment_date ? new Date(payment.payment_date).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric"
    }).replace(/\//g, '-') : "";

    const pDate = payment.payment_date ? new Date(payment.payment_date) : new Date();
    const year = pDate.getFullYear();
    const month = pDate.getMonth();
    const acadYear = month >= 5 ? `${year}-${year + 1}` : `${year - 1}-${year}`;
    const amountVal = Number(payment.amount) || 0;
    const totalVal = gstBreakdown(payment)?.total ?? amountVal;
    const classNameVal = formatClassName(studentDetails ? studentDetails.class_name : (payment.class_name || payment.class));
    const classAndSection = classNameVal;

    const studentName = studentDetails 
      ? `${studentDetails.first_name || ""} ${studentDetails.last_name || ""}`.trim() 
      : `${payment.first_name || ""} ${payment.last_name || ""}`.trim();
    const fatherName = studentDetails 
      ? `${studentDetails.parent_first || ""} ${studentDetails.parent_last || ""}`.trim() 
      : (payment.parent_name || 'N/A');
    const motherName = studentDetails 
      ? `${studentDetails.mother_first_name || ""} ${studentDetails.mother_last_name || ""}`.trim() 
      : (payment.mother_name || 'N/A');
    const admissionNo = studentDetails 
      ? studentDetails.admission_number 
      : (payment.admission_number || 'N/A');
    const fatherMobile = studentDetails 
      ? studentDetails.parent_mobile 
      : (payment.parent_phone || 'N/A');

    printWindow.document.write(`
      <html>
        <head>
          <title>Receipt No ${receiptNoOf(payment)}</title>
          <style>
            body {
              font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif;
              color: #2d3748;
              padding: 20px;
              line-height: 1.4;
              font-size: 13px;
            }
            .receipt-border {
              border: 2px solid #3F51B5;
              border-top: 8px solid #3F51B5;
              border-radius: 6px;
              padding: 20px;
              max-width: 800px;
              margin: 0 auto;
              box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
            }
            .header-table {
              width: 100%;
              border-collapse: collapse;
              margin-bottom: 15px;
            }
            .logo-cell {
              width: 80px;
              vertical-align: middle;
              text-align: left;
            }
            .logo-img {
              width: 70px;
              height: 70px;
              border-radius: 50%;
              object-fit: cover;
              border: 2px solid #3F51B5;
            }
            .school-details-cell {
              text-align: center;
              vertical-align: middle;
              padding-right: 70px;
            }
            .school-name {
              font-size: 24px;
              color: #3F51B5;
              font-weight: bold;
              margin: 0;
              letter-spacing: 0.5px;
            }
            .school-address {
              font-size: 11px;
              color: #718096;
              margin: 5px 0 0 0;
              font-weight: 500;
            }
            .receipt-title-box {
              background-color: #3F51B5;
              color: #ffffff;
              text-align: center;
              padding: 8px 0;
              font-weight: bold;
              font-size: 14px;
              text-transform: uppercase;
              margin-bottom: 20px;
              border-radius: 4px;
            }
            .info-table {
              width: 100%;
              border-collapse: collapse;
              margin-bottom: 20px;
            }
            .info-table td {
              width: 50%;
              padding: 0 10px;
              vertical-align: top;
            }
            .details-card {
              background: #f8fafc;
              border: 1px solid #edf2f7;
              border-radius: 6px;
              padding: 12px;
            }
            .inner-info-table {
              width: 100%;
              border-collapse: collapse;
            }
            .inner-info-table td {
              padding: 6px 0;
              font-size: 12px;
              border-bottom: 1px dashed #e2e8f0;
            }
            .inner-info-table tr:last-child td {
              border-bottom: none;
            }
            .label-col {
              font-weight: 500;
              color: #718096;
              width: 45%;
            }
            .value-col {
              font-weight: bold;
              color: #1a202c;
              text-align: right;
            }
            .particulars-table {
              width: 100%;
              border-collapse: collapse;
              margin-bottom: 0px;
              border-radius: 6px;
              overflow: hidden;
              border: 1px solid #e2e8f0;
            }
            .particulars-table th {
              background-color: #3F51B5;
              color: #ffffff;
              padding: 8px 12px;
              font-weight: bold;
              text-align: left;
              font-size: 12px;
            }
            .particulars-table td {
              padding: 12px;
              height: 80px;
              vertical-align: top;
              border-bottom: 1px solid #e2e8f0;
              color: #2d3748;
            }
            .total-words-box {
              display: table;
              width: 100%;
              border: 1px solid #3F51B5;
              background-color: #f0f2fa;
              margin-bottom: 20px;
              border-radius: 4px;
              overflow: hidden;
            }
            .words-cell {
              display: table-cell;
              padding: 10px 12px;
              font-weight: bold;
              font-size: 12px;
              color: #3F51B5;
              vertical-align: middle;
            }
            .amount-cell {
              display: table-cell;
              width: 120px;
              background-color: #3F51B5;
              color: #ffffff;
              padding: 10px 12px;
              font-weight: bold;
              text-align: right;
              font-size: 14px;
              vertical-align: middle;
            }
            .note-box {
              font-size: 10px;
              line-height: 1.4;
              margin-top: 15px;
              color: #718096;
            }
            .note-title {
              font-weight: bold;
              margin-bottom: 5px;
              color: #4a5568;
            }
            .note-item {
              margin-bottom: 3px;
            }
            .align-right {
              text-align: right;
            }
            @media print {
              body {
                padding: 0;
              }
              .receipt-border {
                box-shadow: none;
                border: 2px solid #3F51B5 !important;
                border-top: 8px solid #3F51B5 !important;
              }
            }
          </style>
        </head>
        <body>
          <div class="receipt-border">
            <table class="header-table">
              <tr>
                <td class="logo-cell">
                  <img src="${schoolLogo}" class="logo-img" alt="Logo" onerror="this.style.display='none'">
                </td>
                <td class="school-details-cell">
                  <h1 class="school-name">${orgName.toUpperCase()}</h1>
                  <p class="school-address">
                    ${addressStr}<br>
                    ${contactStr}
                    ${gstBreakdown(payment)?.gstin ? `<br><b>GSTIN: ${gstBreakdown(payment).gstin}</b>` : ""}
                  </p>
                </td>
              </tr>
            </table>

            <div class="receipt-title-box">
              ONLINE FEE RECEIPT FOR THE ACADEMIC YEAR : ${acadYear}
            </div>

            <table class="info-table">
              <tr>
                <td>
                  <div class="details-card">
                    <table class="inner-info-table">
                      <tr>
                        <td class="label-col">Student Name</td>
                        <td class="value-col">${studentName.toUpperCase()}</td>
                      </tr>
                      <tr>
                        <td class="label-col">Payment Date</td>
                        <td class="value-col">${formattedDate}</td>
                      </tr>
                      <tr>
                        <td class="label-col">Paid By</td>
                        <td class="value-col">${paidByText(payment)}</td>
                      </tr>
                      <tr>
                        <td class="label-col">Fathers name</td>
                        <td class="value-col">${fatherName.toUpperCase()}</td>
                      </tr>
                      <tr>
                        <td class="label-col">Mothers name</td>
                        <td class="value-col">${motherName.toUpperCase()}</td>
                      </tr>
                    </table>
                  </div>
                </td>
                <td>
                  <div class="details-card">
                    <table class="inner-info-table">
                      <tr>
                        <td class="label-col">Admission No</td>
                        <td class="value-col">${admissionNo.toUpperCase()}</td>
                      </tr>
                      <tr>
                        <td class="label-col">Receipt No</td>
                        <td class="value-col">${receiptNoOf(payment)}</td>
                      </tr>
                      <tr>
                        <td class="label-col">Class and Section</td>
                        <td class="value-col">${classAndSection.toUpperCase()}</td>
                      </tr>
                      <tr>
                        <td class="label-col">Father's Mobile No</td>
                        <td class="value-col">${fatherMobile}</td>
                      </tr>
                    </table>
                  </div>
                </td>
              </tr>
            </table>

            <table class="particulars-table">
              <thead>
                <tr>
                  <th style="border-right: 1px solid rgba(255,255,255,0.2);">Particulars</th>
                  <th class="align-right" style="width: 120px; border-right: 1px solid rgba(255,255,255,0.2);">Due (Rs)</th>
                  <th class="align-right" style="width: 120px;">Paid (Rs)</th>
                </tr>
              </thead>
              <tbody>
                ${(() => {
                  const g = gstBreakdown(payment);
                  const due = (Number(payment.fee_amount) || amountVal).toFixed(2);
                  const feeName = payment.fee_name || 'School Fee';
                  if (!g) {
                    return `<tr>
                  <td style="border-right: 1px solid #e2e8f0;">${feeName}</td>
                  <td class="align-right" style="border-right: 1px solid #e2e8f0;">${due}</td>
                  <td class="align-right" style="font-weight: bold;">${amountVal.toFixed(2)}</td>
                </tr>`;
                  }
                  const cell = 'style="height:auto;padding:6px 12px;border-right: 1px solid #e2e8f0;"';
                  return `<tr>
                  <td ${cell}>${feeName}${g.sac ? ` <span style="color:#718096">(SAC ${g.sac})</span>` : ""}<br><span style="font-size:11px;color:#718096">Taxable value</span></td>
                  <td class="align-right" ${cell}>${due}</td>
                  <td class="align-right" style="height:auto;padding:6px 12px;">${g.taxable.toFixed(2)}</td>
                </tr>` + g.lines.map((l) => `<tr>
                  <td ${cell}>${l.label}</td>
                  <td class="align-right" ${cell}></td>
                  <td class="align-right" style="height:auto;padding:6px 12px;">${l.amount.toFixed(2)}</td>
                </tr>`).join("") + (g.exempt ? `<tr>
                  <td ${cell} colspan="3" style="height:auto;padding:6px 12px;font-size:11px;color:#718096;">Educational services — exempt from GST (0%)</td>
                </tr>` : "") + `<tr>
                  <td ${cell}><b>Total${g.exempt ? "" : g.mode === "exclusive" ? " (Fee + GST)" : " (incl. GST)"}</b></td>
                  <td class="align-right" ${cell}></td>
                  <td class="align-right" style="height:auto;padding:6px 12px;font-weight:bold;">${totalVal.toFixed(2)}</td>
                </tr>`;
                })()}
              </tbody>
            </table>

            <div class="total-words-box">
              <div class="words-cell">Total Amount in Words : [ Rupees ${numberToWords(totalVal)} ]</div>
              <div class="amount-cell">Rs.${totalVal.toFixed(2)}</div>
            </div>

            <div class="note-box">
              <div class="note-title">NOTE:</div>
              <div class="note-item">1.This is a Computer Generated Fee Payment Receipt, 'No Signature' is required.</div>
              <div class="note-item">2.Parents are advised to keep the receipt securely as it cannot be issued by School,if misplaced.</div>
              <div class="note-item">3.The fees is subject to Revision as and when advised by the Concerned Authorities and is payable by Parent. The Revision of fees will be notified.</div>
              <div class="note-item">4.Admission Fee is not Refundable.</div>
            </div>
          </div>
          <script>
            window.onload = function() {
              window.print();
              setTimeout(function() { window.close(); }, 500);
            };
          </` + `script>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

    const axiosAuth = axios.create({
        headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json"
        }
    });

  useEffect(() => {
    // Clear right away so a receipt never shows the previous payment's student,
    // and ignore late responses once another receipt has been opened.
    setStudentDetails(null);
    let stale = false;
    const guid = selectedPayment?.student_guid || selectedPayment?.student_id;
    if (!guid) return undefined;

    axiosAuth
      .get(`${apiEndpoints.student}?guid=${guid}`)
      .then((res) => {
        const d = res.data?.status === "success" ? res.data.data : null;
        if (!stale && d && (!d.guid || d.guid === guid)) setStudentDetails(d);
      })
      .catch((error) => console.error("Error fetching student details:", error));

    return () => {
      stale = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPayment]);

  useEffect(() => {
    axiosAuth
      .get(`${apiEndpoints.settings}?action=get_gst`)
      .then((res) => {
        if (res.data?.status === "success" && res.data.data) setGstSettings(res.data.data);
      })
      .catch(() => {}); // settings API not deployed yet → receipts still show 0% GST
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Exclusive GST is collected on top of the fee amount — tell staff the total to collect
  const exclusiveGstHint = (amount) => {
    const rate = Number(gstSettings.rate) || 0;
    const amt = Number(amount) || 0;
    if (!gstSettings.enabled || gstSettings.mode !== "exclusive" || rate <= 0 || amt <= 0) return "";
    const gst = Math.round(amt * rate) / 100;
    return `Collect ₹${(amt + gst).toFixed(2)} (₹${amt.toFixed(2)} fee + ₹${gst.toFixed(2)} GST @ ${rate}%)`;
  };

  // "Cash" or "UPI · Ref XXXX" — from this payment's own record only
  const paidByText = (payment) => {
    const mode = payment?.mode || "cash";
    const ref = payment?.transcation_id || "";
    if (mode === "cash" || ref.startsWith("CASH-")) return "Cash";
    if (!ref || ref.startsWith("BANK-")) return modeLabel(mode);
    return `${modeLabel(mode)} · Ref ${ref}`;
  };

  const localToday = () => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  };

  const emptyForm = () => ({
    transcation_id: "",
    student_guid: "",
    request_id: "",
    amount: "",
    type: "manual",
    mode: "cash",
    payment_date: localToday(),
    payment_status: "success",
  });

  const [form, setForm] = useState(emptyForm);
  const [selectedRequest, setSelectedRequest] = useState(null);

  // Every open/close/save starts from a clean form, so nothing from a
  // previous payment (student, request, amount, transaction ID) carries over.
  const resetAddForm = () => {
    setForm(emptyForm());
    setSelectedRequest(null);
    setErrors({});
  };

  const handleFormChange = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => ({ ...prev, [key]: "" }));
  };

  // Load all fee requests for dropdown
  const loadFeeRequests = async () => {
    try {
      const res = await axiosAuth.get(
        `${apiEndpoints.transactions}?action=get_fee_requests`
      );
      if (res.data?.status !== "success") throw new Error(res.data?.message);
      setFeeRequests(res.data.data || []);
    } catch (err) {
      showSnackbar(err?.message || "Failed to load fee requests", "error");
    }
  };

  // Load all payments
  const loadPayments = async () => {
    try {
      const params = new URLSearchParams({
        action: "get",
        page,
        from,
        to,
        mode: modeFilter,
        type: typeFilter,
        status: statusFilter,
        search: debouncedSearch,
      });
      const res = await axiosAuth.get(`${apiEndpoints.transactions}?${params}`);
      if (res.data?.status !== "success") throw new Error(res.data?.message);

      setPayments(res.data.data.data || []);
      setPages(Math.max(1, Number(res.data.data.pages) || 1));
    } catch (err) {
      showSnackbar(err?.message || "Failed to load transactions", "error");
    }
  };

  // Debounce search so we don't hit the API on every keystroke
  useEffect(() => {
    const t = setTimeout(() => {
      const next = search.trim();
      if (next !== debouncedSearch) {
        setDebouncedSearch(next);
        setPage(1);
      }
    }, 400);
    return () => clearTimeout(t);
  }, [search, debouncedSearch]);

  useEffect(() => {
    loadPayments();
  }, [page, from, to, modeFilter, typeFilter, statusFilter, debouncedSearch]);

  const deletePayment = async (id) => {
    if (!window.confirm("Delete this transaction? The fee request status will be recalculated.")) return;
    try {
      const res = await axiosAuth.post(`${apiEndpoints.transactions}?action=delete`, { id });
      if (res.data?.status !== "success") throw new Error(res.data?.message);
      showSnackbar("Transaction deleted", "success");
      loadPayments();
    } catch (err) {
      showSnackbar(err?.message || "Failed to delete transaction", "error");
    }
  };

  const savePayment = async () => {
    const newErrors = {};

    if (!form.request_id) newErrors.request_id = "Select a request";
    if (!form.student_guid) newErrors.student_guid = "Student not recognized";
    if (!form.amount || Number(form.amount) <= 0)
      newErrors.amount = "Amount must be valid";
    else if (
      form.payment_status === "success" &&
      selectedRequest &&
      Number(selectedRequest.fee_amount) > 0 &&
      Number(form.amount) > Number(selectedRequest.balance)
    )
      newErrors.amount = `Amount exceeds balance of ₹${selectedRequest.balance}`;
    if (!form.payment_date) newErrors.payment_date = "Select payment date";
    const txnId = form.transcation_id.trim();
    const needsRef = form.mode === "card" || form.mode === "upi";
    if (needsRef && !txnId)
      newErrors.transcation_id = `Enter the ${modeLabel(form.mode)} transaction / reference ID, or choose Cash`;
    else if (txnId && form.mode !== "bank" && !/^[A-Za-z0-9][A-Za-z0-9\-_/]{3,49}$/.test(txnId))
      newErrors.transcation_id = "Only letters, numbers, - _ / (4–50 characters)";

    if (Object.keys(newErrors).length) {
      setErrors(newErrors);
      return;
    }

    setSaving(true);
    try {
      const payload = {
        ...form,
        transcation_id: form.mode === "bank" ? "" : txnId,
        // no reference → cash paid, except bank transfer which never asks for one
        mode: txnId || form.mode === "bank" ? form.mode : "cash",
      };
      const res = await axiosAuth.post(`${apiEndpoints.transactions}?action=add`, payload);
      if (res.data?.status !== "success") throw new Error(res.data?.message);
      showSnackbar(res.data.message || "Transaction saved", "success");
    } catch (err) {
      showSnackbar(err?.message || "Failed to save transaction", "error");
      return;
    } finally {
      setSaving(false);
    }

    setOpenAdd(false);
    resetAddForm();
    loadPayments();
  };

  const handleRequestChange = (e, value) => {
    if (!value) {
      setSelectedRequest(null);
      setForm((prev) => ({ ...prev, request_id: "", student_guid: "", amount: "" }));
      return;
    }

    setSelectedRequest(value);
    setForm((prev) => ({
      ...prev,
      request_id: value.request_id,
      student_guid: value.student_guid,
      amount: value.balance ?? value.fee_amount,
    }));
    setErrors((prev) => ({ ...prev, request_id: "", amount: "" }));
  };

  const statusColor = (s) =>
    s === "success" ? "success" : s === "failed" ? "error" : "warning";

  return (
    <Box sx={{ p: 2 }}>
      {/* HEADER */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          flexWrap: "wrap",
          alignItems: "center",
          mb: 2,
          gap: 2,
        }}
      >
        <Typography variant="h6" fontWeight={700}>
          Transactions
        </Typography>

        <Button
          variant="contained"
          startIcon={<Add />}
          sx={{ borderRadius: 3, px: 3 }}
          onClick={() => {
            resetAddForm();
            loadFeeRequests();
            setOpenAdd(true);
          }}
        >
          Add Transaction
        </Button>
      </Box>

      {/* FILTERS */}
      <Paper
        sx={{
          p: 2,
          mb: 3,
          borderRadius: 3,
          background: "rgba(255,255,255,0.5)",
          backdropFilter: "blur(12px)",
          border: "1px solid rgba(255,255,255,0.5)",
          boxShadow: "0 10px 24px rgba(15, 23, 42, 0.12)",
        }}
      >
        <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
          <TextField
            type="date"
            label="From"
            size="small"
            InputLabelProps={{ shrink: true }}
            InputProps={{ sx: { borderRadius: 3 } }}
            value={from}
            onChange={(e) => { setFrom(e.target.value); setPage(1); }}
            sx={{ minWidth: 160, width: { xs: "100%", md: 160 } }}
          />

          <TextField
            type="date"
            label="To"
            size="small"
            InputLabelProps={{ shrink: true }}
            value={to}
            onChange={(e) => { setTo(e.target.value); setPage(1); }}
            sx={{ minWidth: 160, width: { xs: "100%", md: 160 } }}
            InputProps={{ sx: { borderRadius: 3 } }}
          />

          <TextField
            select
            label="Mode"
            size="small"
            value={modeFilter}
            onChange={(e) => { setModeFilter(e.target.value); setPage(1); }}
            sx={{ minWidth: 140, width: { xs: "100%", md: 160 } }}
            InputProps={{ sx: { borderRadius: 3 } }}
          >
            <MenuItem value="">All</MenuItem>
            {PAYMENT_MODES.map((m) => (
              <MenuItem key={m.value} value={m.value}>
                {m.label}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            select
            label="Type"
            size="small"
            value={typeFilter}
            onChange={(e) => { setTypeFilter(e.target.value); setPage(1); }}
            sx={{ minWidth: 140, width: { xs: "100%", md: 140 } }}
            InputProps={{ sx: { borderRadius: 3 } }}
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="manual">Manual</MenuItem>
            <MenuItem value="gateway">Gateway</MenuItem>
          </TextField>

          <TextField
            select
            label="Status"
            size="small"
            value={statusFilter}
            onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
            sx={{ minWidth: 140, width: { xs: "100%", md: 140 } }}
            InputProps={{ sx: { borderRadius: 3 } }}
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="success">Success</MenuItem>
            <MenuItem value="failed">Failed</MenuItem>
            <MenuItem value="pending">Pending</MenuItem>
          </TextField>

          <TextField
            placeholder="Search student / receipt no..."
            size="small"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{
              minWidth: { md: 200, lg: 220 },
              width: { xs: "100%", md: 200 },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
              sx: { borderRadius: 3 },
            }}
          />
        </Box>
      </Paper>

      {/* TABLE */}
      <Paper
        sx={{
          borderRadius: 3,
          overflow: "hidden",
          background: "rgba(255,255,255,0.5)",
          backdropFilter: "blur(12px)",
          border: "1px solid rgba(255,255,255,0.5)",
          boxShadow: "0 10px 24px rgba(15, 23, 42, 0.12)",
        }}
      >
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ background: "rgba(0,0,0,0.04)" }}>
                <TableCell>Receipt No</TableCell>
                <TableCell>Txn / Ref ID</TableCell>
                <TableCell>Student</TableCell>
                <TableCell>Request</TableCell>
                <TableCell>Date</TableCell>
                <TableCell>Mode</TableCell>
                <TableCell>Amount</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="center">Receipt</TableCell>
                <TableCell align="center">Delete</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {payments.map((p) => (
                <TableRow key={p.id} hover>
                  <TableCell sx={{ fontWeight: 600, whiteSpace: "nowrap" }}>
                    {p.receipt_no ? receiptNoOf(p) : "-"}
                  </TableCell>
                  <TableCell>{p.transcation_id || "-"}</TableCell>
                  <TableCell>
                    {p.first_name} {p.last_name}
                  </TableCell>
                  <TableCell>{p.request_id}</TableCell>
                  <TableCell>{p.payment_date}</TableCell>
                  <TableCell>{modeLabel(p.mode)}</TableCell>
                  <TableCell>₹{p.amount}</TableCell>
                  <TableCell>
                    <Chip
                      label={p.payment_status}
                      color={statusColor(p.payment_status)}
                      size="small"
                    />
                  </TableCell>

                  <TableCell align="center">
                    {p.payment_status === "success" && (
                      <IconButton
                        color="primary"
                        onClick={() => {
                          setSelectedPayment(p);
                          setOpenReceipt(true);
                        }}
                        title="View Receipt"
                      >
                        <ReceiptLong />
                      </IconButton>
                    )}
                  </TableCell>

                  <TableCell align="center">
                    <IconButton
                      color="error"
                      title="Delete"
                      onClick={() => deletePayment(p.id)}
                    >
                      <Delete />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* PAGINATION */}
      <Box sx={{ display: "flex", justifyContent: "center", mt: 3, gap: 2 }}>
        <Button disabled={page === 1} onClick={() => setPage(page - 1)}>
          Prev
        </Button>

        <Typography>
          Page {page} / {pages}
        </Typography>

        <Button disabled={page >= pages} onClick={() => setPage(page + 1)}>
          Next
        </Button>
      </Box>

      {/* ADD TRANSACTION MODAL */}
      <Modal
        open={openAdd}
        onClose={() => {
          if (saving) return;
          setOpenAdd(false);
          resetAddForm();
        }}
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: { xs: "90%", sm: 420 },
            bgcolor: "white",
            p: 3,
            borderRadius: 3,
            boxShadow: 5,
          }}
        >
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 700 }}>
            Add Transaction
          </Typography>

          {/* Mode */}
          <TextField
            select
            fullWidth
            label="Mode"
            size="small"
            sx={{ mb: 2 }}
            value={form.mode}
            onChange={(e) => handleFormChange("mode", e.target.value)}
            InputProps={{ sx: { borderRadius: 3 } }}
          >
            {PAYMENT_MODES.map((m) => (
              <MenuItem key={m.value} value={m.value}>
                {m.label}
              </MenuItem>
            ))}
          </TextField>

          {/* Transaction ID — this payment's own reference, never another payment's.
              Not asked for bank transfers: a BANK- receipt number is generated. */}
          {form.mode === "bank" ? (
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: -1, mb: 2 }}>
              No reference needed — a BANK receipt number is generated.
            </Typography>
          ) : (
          <TextField
            fullWidth
            label={
              form.mode === "cash"
                ? "Transaction ID (optional)"
                : `${modeLabel(form.mode)} Transaction / Reference ID`
            }
            required={form.mode !== "cash"}
            size="small"
            sx={{ mb: 2 }}
            value={form.transcation_id}
            autoComplete="off"
            error={Boolean(errors.transcation_id)}
            helperText={
              errors.transcation_id ||
              (form.mode === "cash"
                ? "Leave blank for cash — a CASH receipt number is generated"
                : "Enter the reference shown in the UPI app / card slip")
            }
            onChange={(e) => handleFormChange("transcation_id", e.target.value.toUpperCase())}
            InputProps={{ sx: { borderRadius: 3 } }}
          />
          )}

          {/* Request selection */}
          <Autocomplete
            options={feeRequests}
            value={selectedRequest}
            getOptionLabel={(option) => option?.display_text || ""}
            isOptionEqualToValue={(option, value) =>
              option.request_id === value.request_id
            }
            onChange={handleRequestChange}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Select Request"
                size="small"
                sx={{ mb: 2 ,borderRadius:3}}
                error={Boolean(errors.request_id)}
                helperText={errors.request_id}
                
              />
            )}
          />

          {/* Amount */}
          <TextField
            fullWidth
            label="Amount"
            size="small"
            type="number"
            sx={{ mb: 2 }}
            value={form.amount}
            error={Boolean(errors.amount)}
            helperText={
              errors.amount ||
              [
                selectedRequest && Number(selectedRequest.paid_amount) > 0
                  ? `Paid ₹${selectedRequest.paid_amount} of ₹${selectedRequest.fee_amount} · Balance ₹${selectedRequest.balance}`
                  : "",
                exclusiveGstHint(form.amount),
              ]
                .filter(Boolean)
                .join(" · ")
            }
            onChange={(e) => handleFormChange("amount", e.target.value)}
            InputProps={{ sx: { borderRadius: 3 } }}
          />

          {/* Type */}
          <TextField
            select
            fullWidth
            label="Type"
            size="small"
            sx={{ mb: 2 }}
            value={form.type}
            onChange={(e) => handleFormChange("type", e.target.value)}
            InputProps={{ sx: { borderRadius: 3 } }}
          >
            <MenuItem value="manual">Manual</MenuItem>
            <MenuItem value="gateway">Gateway</MenuItem>
          </TextField>

          {/* Payment date */}
          <TextField
            type="date"
            fullWidth
            label="Payment Date"
            size="small"
            sx={{ mb: 2 }}
            InputLabelProps={{ shrink: true }}
            value={form.payment_date}
            error={Boolean(errors.payment_date)}
            helperText={errors.payment_date}
            onChange={(e) => handleFormChange("payment_date", e.target.value)}
            InputProps={{ sx: { borderRadius: 3 } }}
          />

          {/* Status */}
          <TextField
            select
            fullWidth
            label="Status"
            size="small"
            sx={{ mb: 2 }}
            value={form.payment_status}
            onChange={(e) => handleFormChange("payment_status", e.target.value)}
            InputProps={{ sx: { borderRadius: 3 } }}
          >
            <MenuItem value="success">Success</MenuItem>
            <MenuItem value="failed">Failed</MenuItem>
            <MenuItem value="pending">Pending</MenuItem>
          </TextField>

          <Button
            variant="contained"
            fullWidth
            onClick={savePayment}
            disabled={saving}
            sx={{ borderRadius: 999, px: 3 }}
          >
            {saving ? "Saving..." : "Save Transaction"}
          </Button>
        </Box>
      </Modal>

      {/* RECEIPT PREVIEW MODAL */}
      <Modal
        open={openReceipt}
        onClose={() => {
          setOpenReceipt(false);
          setSelectedPayment(null);
        }}
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: { xs: "95%", sm: 700 },
            maxHeight: "90vh",
            overflowY: "auto",
            bgcolor: "white",
            p: { xs: 2, sm: 4 },
            borderRadius: 3,
            boxShadow: 24,
            display: "flex",
            flexDirection: "column",
            gap: 2,
          }}
        >
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Typography variant="h6" fontWeight={700} color="primary">
              Fee E-Receipt Preview
            </Typography>
            <Chip label="PAID" color="success" variant="outlined" size="small" />
          </Box>
          <Divider />          {selectedPayment && (() => {
            const loggedInUser = JSON.parse(localStorage.getItem("user") || "{}");
            const schoolLogo = window.location.origin + '/schoollogo.png';
            const orgName = loggedInUser.organization_name || "ROOTS MONTESSORI SCHOOL";
            
            const addressStr = [
              loggedInUser.address,
              loggedInUser.city,
              loggedInUser.state,
              loggedInUser.country,
              loggedInUser.pincode ? `PIN: ${loggedInUser.pincode}` : ""
            ].filter(Boolean).join(", ") || "12, Green Street, Bangalore, Karnataka 560102";
            
            const contactStr = [
              loggedInUser.phone ? `Phone: ${loggedInUser.phone}` : "",
              loggedInUser.email ? `Email: ${loggedInUser.email}` : ""
            ].filter(Boolean).join(" | ") || "Phone No. : 080-2223344 | Email: info@rootsmontessori.com";

            const pDate = selectedPayment.payment_date ? new Date(selectedPayment.payment_date) : new Date();
            const acadYear = pDate.getMonth() >= 5 ? `${pDate.getFullYear()}-${pDate.getFullYear() + 1}` : `${pDate.getFullYear() - 1}-${pDate.getFullYear()}`;
            const amountVal = Number(selectedPayment.amount) || 0;
            const totalVal = gstBreakdown(selectedPayment)?.total ?? amountVal;
            const formattedDate = selectedPayment.payment_date ? new Date(selectedPayment.payment_date).toLocaleDateString("en-GB", {
              day: "2-digit",
              month: "2-digit",
              year: "numeric"
            }).replace(/\//g, '-') : "";
            const classNameVal = formatClassName(studentDetails ? studentDetails.class_name : (selectedPayment.class_name || selectedPayment.class));

            const studentName = studentDetails 
              ? `${studentDetails.first_name || ""} ${studentDetails.last_name || ""}`.trim() 
              : `${selectedPayment.first_name || ""} ${selectedPayment.last_name || ""}`.trim();
            const fatherName = studentDetails 
              ? `${studentDetails.parent_first || ""} ${studentDetails.parent_last || ""}`.trim() 
              : (selectedPayment.parent_name || 'N/A');
            const motherName = studentDetails 
              ? `${studentDetails.mother_first_name || ""} ${studentDetails.mother_last_name || ""}`.trim() 
              : (selectedPayment.mother_name || 'N/A');
            const admissionNo = studentDetails 
              ? studentDetails.admission_number 
              : (selectedPayment.admission_number || 'N/A');
            const fatherMobile = studentDetails 
              ? studentDetails.parent_mobile 
              : (selectedPayment.parent_phone || 'N/A');

            return (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                {/* E-Receipt Border Wrapper to match print layout */}
                <Box
                  sx={{
                    border: "2px solid #3F51B5",
                    borderTop: "8px solid #3F51B5",
                    borderRadius: 2,
                    p: 2.5,
                    fontFamily: "'Montserrat', sans-serif",
                    color: "#2d3748",
                    display: "flex",
                    flexDirection: "column",
                    gap: 2.5,
                    boxShadow: "0 4px 12px rgba(63, 81, 181, 0.08)"
                  }}
                >
                  {/* Header Row */}
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <Box
                      component="img"
                      src={schoolLogo}
                      alt="Logo"
                      onError={(e) => { e.target.style.display = 'none'; }}
                      sx={{ width: 65, height: 65, borderRadius: "50%", objectFit: "cover", border: "2px solid #3F51B5" }}
                    />
                    <Box sx={{ textAlign: "center", flexGrow: 1, pr: 8 }}>
                      <Typography variant="subtitle1" fontWeight="bold" sx={{ fontSize: "18px", m: 0, color: "#3F51B5", letterSpacing: "0.5px" }}>
                        {orgName.toUpperCase()}
                      </Typography>
                      <Typography variant="caption" sx={{ fontSize: "10px", fontWeight: 500, color: "#718096", display: "block", mt: 0.5 }}>
                        {addressStr}
                      </Typography>
                      <Typography variant="caption" sx={{ fontSize: "10px", fontWeight: 500, color: "#718096", display: "block" }}>
                        {contactStr}
                      </Typography>
                      {gstBreakdown(selectedPayment)?.gstin && (
                        <Typography variant="caption" sx={{ fontSize: "10px", fontWeight: 700, color: "#4a5568", display: "block" }}>
                          GSTIN: {gstBreakdown(selectedPayment).gstin}
                        </Typography>
                      )}
                    </Box>
                  </Box>

                  {/* Title Banner */}
                  <Box
                    sx={{
                      backgroundColor: "#3F51B5",
                      color: "#ffffff",
                      textAlign: "center",
                      py: 1,
                      fontWeight: "bold",
                      fontSize: "12px",
                      textTransform: "uppercase",
                      borderRadius: 1,
                      boxShadow: "0 2px 4px rgba(63, 81, 181, 0.15)"
                    }}
                  >
                    ONLINE FEE RECEIPT FOR THE ACADEMIC YEAR : {acadYear}
                  </Box>

                  {/* Student Details Grid */}
                  <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2, fontSize: "12px" }}>
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.8, p: 1.5, bgcolor: "#f8fafc", borderRadius: 1.5, border: "1px solid #edf2f7" }}>
                      <Box sx={{ display: "flex", justifyContent: "space-between", borderBottom: "1px dashed #e2e8f0", pb: 0.5 }}>
                        <Typography sx={{ fontSize: "12px", color: "#718096", fontWeight: 500 }}>Student Name</Typography>
                        <Typography sx={{ fontWeight: "bold", fontSize: "12px", color: "#1a202c" }}>
                          {studentName.toUpperCase()}
                        </Typography>
                      </Box>
                      <Box sx={{ display: "flex", justifyContent: "space-between", borderBottom: "1px dashed #e2e8f0", pb: 0.5 }}>
                        <Typography sx={{ fontSize: "12px", color: "#718096", fontWeight: 500 }}>Payment Date</Typography>
                        <Typography sx={{ fontWeight: "bold", fontSize: "12px", color: "#1a202c" }}>{formattedDate}</Typography>
                      </Box>
                      <Box sx={{ display: "flex", justifyContent: "space-between", borderBottom: "1px dashed #e2e8f0", pb: 0.5 }}>
                        <Typography sx={{ fontSize: "12px", color: "#718096", fontWeight: 500 }}>Paid By</Typography>
                        <Typography sx={{ fontWeight: "bold", fontSize: "12px", color: "#1a202c" }}>{paidByText(selectedPayment)}</Typography>
                      </Box>
                      <Box sx={{ display: "flex", justifyContent: "space-between", borderBottom: "1px dashed #e2e8f0", pb: 0.5 }}>
                        <Typography sx={{ fontSize: "12px", color: "#718096", fontWeight: 500 }}>Father's name</Typography>
                        <Typography sx={{ fontWeight: "bold", fontSize: "12px", color: "#1a202c" }}>
                          {fatherName.toUpperCase()}
                        </Typography>
                      </Box>
                      <Box sx={{ display: "flex", justifyContent: "space-between", pb: 0.2 }}>
                        <Typography sx={{ fontSize: "12px", color: "#718096", fontWeight: 500 }}>Mother's name</Typography>
                        <Typography sx={{ fontWeight: "bold", fontSize: "12px", color: "#1a202c" }}>
                          {motherName.toUpperCase()}
                        </Typography>
                      </Box>
                    </Box>

                    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.8, p: 1.5, bgcolor: "#f8fafc", borderRadius: 1.5, border: "1px solid #edf2f7" }}>
                      <Box sx={{ display: "flex", justifyContent: "space-between", borderBottom: "1px dashed #e2e8f0", pb: 0.5 }}>
                        <Typography sx={{ fontSize: "12px", color: "#718096", fontWeight: 500 }}>Admission No</Typography>
                        <Typography sx={{ fontWeight: "bold", fontSize: "12px", color: "#1a202c" }}>
                          {admissionNo.toUpperCase()}
                        </Typography>
                      </Box>
                      <Box sx={{ display: "flex", justifyContent: "space-between", borderBottom: "1px dashed #e2e8f0", pb: 0.5 }}>
                        <Typography sx={{ fontSize: "12px", color: "#718096", fontWeight: 500 }}>Receipt No</Typography>
                        <Typography sx={{ fontWeight: "bold", fontSize: "12px", color: "#1a202c" }}>
                          {receiptNoOf(selectedPayment)}
                        </Typography>
                      </Box>
                      <Box sx={{ display: "flex", justifyContent: "space-between", borderBottom: "1px dashed #e2e8f0", pb: 0.5 }}>
                        <Typography sx={{ fontSize: "12px", color: "#718096", fontWeight: 500 }}>Class and Section</Typography>
                        <Typography sx={{ fontWeight: "bold", fontSize: "12px", color: "#1a202c" }}>
                          {classNameVal.toUpperCase()}
                        </Typography>
                      </Box>
                      <Box sx={{ display: "flex", justifyContent: "space-between", pb: 0.2 }}>
                        <Typography sx={{ fontSize: "12px", color: "#718096", fontWeight: 500 }}>Father's Mobile No</Typography>
                        <Typography sx={{ fontWeight: "bold", fontSize: "12px", color: "#1a202c" }}>
                          {fatherMobile}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>

                  {/* Particulars Table */}
                  <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 1.5, borderColor: "#e2e8f0", overflow: "hidden" }}>
                    <Table size="small">
                      <TableHead sx={{ bgcolor: "#3F51B5" }}>
                        <TableRow>
                          <TableCell sx={{ fontWeight: "bold", color: "#ffffff", borderRight: "1px solid rgba(255,255,255,0.2)" }}>
                            Particulars
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: "bold", color: "#ffffff", borderRight: "1px solid rgba(255,255,255,0.2)", width: 100 }}>
                            Due
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: "bold", color: "#ffffff", width: 100 }}>
                            Paid (Rs)
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {(() => {
                          const g = gstBreakdown(selectedPayment);
                          const cellSx = { borderRight: "1px solid #e2e8f0", color: "#2d3748" };
                          if (!g) {
                            return (
                              <TableRow hover>
                                <TableCell sx={{ ...cellSx, height: 60, verticalAlign: "top" }}>
                                  {selectedPayment.fee_name || 'School Fee'}
                                </TableCell>
                                <TableCell align="right" sx={{ ...cellSx, verticalAlign: "top" }}>
                                  {(Number(selectedPayment.fee_amount) || amountVal).toFixed(2)}
                                </TableCell>
                                <TableCell align="right" sx={{ verticalAlign: "top", color: "#1a202c", fontWeight: "bold" }}>
                                  {amountVal.toFixed(2)}
                                </TableCell>
                              </TableRow>
                            );
                          }
                          return (
                            <>
                              <TableRow>
                                <TableCell sx={cellSx}>
                                  {selectedPayment.fee_name || 'School Fee'}
                                  {g.sac ? ` (SAC ${g.sac})` : ""}
                                  <Typography variant="caption" display="block" color="text.secondary">
                                    Taxable value
                                  </Typography>
                                </TableCell>
                                <TableCell align="right" sx={cellSx}>
                                  {(Number(selectedPayment.fee_amount) || amountVal).toFixed(2)}
                                </TableCell>
                                <TableCell align="right">{g.taxable.toFixed(2)}</TableCell>
                              </TableRow>
                              {g.lines.map((l) => (
                                <TableRow key={l.label}>
                                  <TableCell sx={cellSx}>{l.label}</TableCell>
                                  <TableCell sx={cellSx} />
                                  <TableCell align="right">{l.amount.toFixed(2)}</TableCell>
                                </TableRow>
                              ))}
                              {g.exempt && (
                                <TableRow>
                                  <TableCell colSpan={3} sx={{ fontSize: 11, color: "text.secondary", py: 0.75 }}>
                                    Educational services — exempt from GST (0%)
                                  </TableCell>
                                </TableRow>
                              )}
                              <TableRow>
                                <TableCell sx={{ ...cellSx, fontWeight: "bold" }}>
                                  {g.exempt ? "Total" : g.mode === "exclusive" ? "Total (Fee + GST)" : "Total (incl. GST)"}
                                </TableCell>
                                <TableCell sx={cellSx} />
                                <TableCell align="right" sx={{ fontWeight: "bold", color: "#1a202c" }}>
                                  {/* exclusive GST: fee + GST collected (same as the print and the words box) */}
                                  {totalVal.toFixed(2)}
                                </TableCell>
                              </TableRow>
                            </>
                          );
                        })()}
                      </TableBody>
                    </Table>
                  </TableContainer>

                  {/* Total summary row */}
                  <Box
                    sx={{
                      border: "1px solid #3F51B5",
                      borderRadius: 1.5,
                      display: "flex",
                      fontSize: "12px",
                      overflow: "hidden",
                      bgcolor: "#f0f2fa",
                    }}
                  >
                    <Box sx={{ flexGrow: 1, p: 1.2, fontWeight: "bold", color: "#3F51B5" }}>
                      Total Amount in Words : [ Rupees {numberToWords(totalVal)} ]
                    </Box>
                    <Box sx={{ width: 100, p: 1.2, fontWeight: "bold", textAlign: "right", borderLeft: "1px solid #3F51B5", bgcolor: "#3F51B5", color: "#ffffff" }}>
                      Rs.{totalVal.toFixed(2)}
                    </Box>
                  </Box>

                  {/* Note section */}
                  <Box sx={{ fontSize: "10px", mt: 1, display: "flex", flexDirection: "column", gap: 0.4, color: "#718096" }}>
                    <Typography variant="caption" sx={{ fontWeight: "bold", fontSize: "10px", color: "#4a5568" }}>NOTE:</Typography>
                    <Typography variant="caption" sx={{ fontSize: "10px" }}>
                      1.This is a Computer Generated Fee Payment Receipt, 'No Signature' is required.
                    </Typography>
                    <Typography variant="caption" sx={{ fontSize: "10px" }}>
                      2.Parents are advised to keep the receipt securely as it cannot be issued by School,if misplaced.
                    </Typography>
                    <Typography variant="caption" sx={{ fontSize: "10px" }}>
                      3.The fees is subject to Revision as and when advised by the Concerned Authorities and is payable by Parent. The Revision of fees will be notified.
                    </Typography>
                    <Typography variant="caption" sx={{ fontSize: "10px" }}>
                      4.Admission Fee is not Refundable.
                    </Typography>
                  </Box>
                </Box>

                {/* Actions row */}
                <Box sx={{ display: "flex", gap: 2, mt: 1 }}>
                  <Button
                    variant="outlined"
                    fullWidth
                    onClick={() => {
                      setOpenReceipt(false);
                      setSelectedPayment(null);
                    }}
                    sx={{ borderRadius: 999 }}
                  >
                    Close
                  </Button>
                  <Button
                    variant="contained"
                    fullWidth
                    startIcon={<Print />}
                    onClick={() => handlePrint(selectedPayment, studentDetails)}
                    sx={{ borderRadius: 999 }}
                  >
                    Print / Export PDF
                  </Button>
                </Box>
              </Box>
            );
          })()}
        </Box>
      </Modal>
    </Box>
  );
};

export default Payments;
